import React, { useState } from 'react';
import { ZONES, BRAND } from '../data/constants';
import { useApp } from '../data/ColisContext';
import { inputStyle, labelStyle, focusInput, blurInput } from '../utils/helpers';
import { apiUrl, parseApiResponse } from '../utils/api';

const INITIAL = {
  nom:         '',
  tel:         '',
  destinataire:'',
  adresse:     '',
  zone:        '',
};

export default function FormDepot({ onSuccess }) {
  const { addColis, setClientTel, user } = useApp();
  const [form, setForm]       = useState(INITIAL);
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [waColisList, setWaColisList] = useState([]);
  const [waSelectedId, setWaSelectedId] = useState('');
  const [creatingBulk, setCreatingBulk] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      setClientTel(form.tel);
      await addColis({
        nom: form.nom,
        tel: form.tel,
        destinataire: form.destinataire,
        adresse: form.adresse,
        zone: form.zone,
        photo: photoFile || undefined,
      });
      setForm(INITIAL);
      setPhotoFile(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      alert(err?.message || 'Erreur lors de la création du colis');
    } finally {
      setLoading(false);
    }
  };

  const importFromWhatsApp = async () => {
    setImporting(true);
    try {
      const res = await fetch(apiUrl('whatsapp/latest'), {
        method: 'GET',
        credentials: 'include',
      });
      const out = await parseApiResponse(res);
      if (!out.ok) {
        const msg = out?.json?.message || out?.text || 'Erreur serveur';
        throw new Error(String(msg));
      }
      const data = out?.json || {};
      const list = Array.isArray(data?.colis_list) ? data.colis_list : (data?.colis ? [data.colis] : []);
      if (list.length === 0) throw new Error('Aucun colis WhatsApp trouvé');

      setWaColisList(list);
      const first = list[0];
      setWaSelectedId(first?.id || '');

      setForm(f => ({
        ...f,
        nom: first?.nom || '',
        destinataire: first?.destinataire || '',
        tel: first?.tel || '',
        adresse: first?.adresse || '',
        zone: first?.zone || '',
      }));
    } catch (err) {
      alert(err?.message || 'Impossible d\'importer');
    } finally {
      setImporting(false);
    }
  };

  const applySelectedWhatsAppColis = () => {
    if (!waSelectedId) return;
    const selected = waColisList.find(c => c?.id === waSelectedId);
    if (!selected) return;
    setForm(f => ({
      ...f,
      nom: selected?.nom || '',
      destinataire: selected?.destinataire || '',
      tel: selected?.tel || '',
      adresse: selected?.adresse || '',
      zone: selected?.zone || '',
    }));
  };

  const createAllWhatsAppColis = async () => {
    if (!waColisList || waColisList.length === 0) return;
    setCreatingBulk(true);
    try {
      const importedRaw = localStorage.getItem('zaki_imported_wa_colis_ids');
      const imported = new Set(importedRaw ? JSON.parse(importedRaw) : []);

      let createdCount = 0;
      let skippedCount = 0;
      for (const c of waColisList) {
        const sourceId = c?.id || '';
        if (sourceId && imported.has(sourceId)) {
          skippedCount += 1;
          continue;
        }

        const newColis = await addColis({
          nom: c?.nom || '',
          tel: c?.tel || '',
          destinataire: c?.destinataire || '',
          adresse: c?.adresse || '',
          zone: c?.zone || '',
          source: 'whatsapp',
          meta: sourceId ? { wa_source_id: sourceId } : undefined,
        });

        if (sourceId) imported.add(sourceId);
        if (newColis?.tel) setClientTel(newColis.tel);
        createdCount += 1;
      }

      localStorage.setItem('zaki_imported_wa_colis_ids', JSON.stringify(Array.from(imported)));
      setWaColisList([]);
      setWaSelectedId('');
      setForm(INITIAL);
      alert(`Créés: ${createdCount}${skippedCount ? ` (déjà importés: ${skippedCount})` : ''}`);
      if (onSuccess) onSuccess();
    } catch (err) {
      alert(err?.message || 'Impossible de créer les colis');
    } finally {
      setCreatingBulk(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      <button type="button" onClick={importFromWhatsApp} disabled={importing || loading} style={{
        padding:      '12px 14px',
        background:   importing ? '#e2e8f0' : '#0EA5E9',
        color:        'white',
        border:       'none',
        borderRadius: 14,
        fontWeight:   800,
        fontSize:     14,
        cursor:       importing || loading ? 'not-allowed' : 'pointer',
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'center',
        gap:          10,
        transition:   'all 0.2s',
        boxShadow:    importing ? 'none' : '0 4px 16px rgba(14,165,233,0.35)',
        fontFamily:   'inherit',
      }}>
        {importing ? 'Import...' : 'Importer depuis WhatsApp'}
      </button>

      {waColisList.length > 0 && (
        <button type="button" onClick={createAllWhatsAppColis} disabled={creatingBulk || importing || loading} style={{
          padding:      '12px 14px',
          background:   creatingBulk ? '#e2e8f0' : '#16A34A',
          color:        'white',
          border:       'none',
          borderRadius: 14,
          fontWeight:   900,
          fontSize:     14,
          cursor:       creatingBulk || importing || loading ? 'not-allowed' : 'pointer',
          display:      'flex',
          alignItems:   'center',
          justifyContent: 'center',
          gap:          10,
          transition:   'all 0.2s',
          boxShadow:    creatingBulk ? 'none' : '0 4px 16px rgba(22,163,74,0.35)',
          fontFamily:   'inherit',
        }}>
          {creatingBulk ? 'Création...' : `Créer tous les colis WhatsApp (${waColisList.length})`}
        </button>
      )}

      {waColisList.length > 1 && (
        <div style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
          background: '#F1F5F9',
          borderRadius: 14,
          padding: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#334155' }}>
            Choisir un colis WhatsApp ({waColisList.length})
          </div>

          <select
            value={waSelectedId}
            onChange={(e) => setWaSelectedId(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer', maxWidth: 420 }}
          >
            {waColisList.map(c => (
              <option key={c.id} value={c.id}>
                {c.nom || 'Colis'} — {c.destinataire || ''} — {c.tel || ''}
              </option>
            ))}
          </select>

          <button type="button" onClick={applySelectedWhatsAppColis} disabled={loading || importing} style={{
            padding: '10px 12px',
            borderRadius: 12,
            border: 'none',
            background: '#E0F2FE',
            color: '#0369A1',
            fontWeight: 900,
            cursor: loading || importing ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}>
            Remplir
          </button>
        </div>
      )}

      <Field label="Nom du colis *" id="nom">
        <input id="nom" required style={inputStyle} placeholder="Ex: Chaussures, documents..."
          value={form.nom} onChange={set('nom')} onFocus={focusInput} onBlur={blurInput} />
      </Field>

      <Row>
        <Field label="Destinataire *" id="destinataire">
          <input id="destinataire" required style={inputStyle} placeholder="Nom du destinataire"
            value={form.destinataire} onChange={set('destinataire')} onFocus={focusInput} onBlur={blurInput} />
        </Field>
        <Field label="Téléphone *" id="tel">
          <input id="tel" required style={inputStyle} placeholder="77 xxx xx xx"
            value={form.tel} onChange={set('tel')} onFocus={focusInput} onBlur={blurInput} />
        </Field>
      </Row>

      <Row>
        <Field label="Adresse livraison *" id="adresse">
          <input id="adresse" required style={inputStyle} placeholder="Ex: Villa 24, Rue 10..."
            value={form.adresse} onChange={set('adresse')} onFocus={focusInput} onBlur={blurInput} />
        </Field>
        <Field label="Zone" id="zone">
          <select id="zone" style={{ ...inputStyle, cursor: 'pointer' }}
            value={form.zone} onChange={set('zone')} onFocus={focusInput} onBlur={blurInput}>
            <option value="">Choisir une zone...</option>
            {ZONES.map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </Field>
      </Row>

      <Field label="Photo du produit" id="photo">
        <input
          id="photo"
          type="file"
          accept="image/*"
          style={inputStyle}
          onChange={(e) => setPhotoFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
          onFocus={focusInput}
          onBlur={blurInput}
        />
        {photoFile && (
          <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: '#475569' }}>
            {photoFile.name} ({Math.round((photoFile.size || 0) / 1024)} KB)
          </div>
        )}
      </Field>

      <button type="submit" disabled={loading} style={{
        padding:      '14px',
        background:   loading ? '#c7d2fe' : BRAND.gradient,
        color:        'white',
        border:       'none',
        borderRadius: 14,
        fontWeight:   700,
        fontSize:     15,
        cursor:       loading ? 'not-allowed' : 'pointer',
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'center',
        gap:          10,
        transition:   'all 0.2s',
        marginTop:    4,
        boxShadow:    loading ? 'none' : '0 4px 16px rgba(79,70,229,0.35)',
        fontFamily:   'inherit',
      }}>
        {loading
          ? <><Spinner /> Enregistrement...</>
          : <>📦 Enregistrer le colis</>
        }
      </button>
    </form>
  );
}

function Row({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
      {children}
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <span style={{
      width: 16, height: 16,
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: 'white',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'spin 0.6s linear infinite',
    }} />
  );
}
